import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BarChart3, Clock, CheckCircle, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import localApiAdapter from '../../core/services/localApiAdapter';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import { pipelineFromKpis } from '../../shared/components/dashboard/moduleDashboardCharts';
import { entityLabel, normalizeStatusValue } from './approbationsShared';

const NS = 'analysesPage';

function countStatus(records, ...statuses) {
  const set = new Set(statuses);
  return records.filter((r) => set.has(normalizeStatusValue(r.status))).length;
}

export default function ApprobationsAnalysesPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.approbations.pages.${NS}.${key}`, opts);
  const pl = (key, opts) => t(`modules.approbations.pages.common.${key}`, opts);

  const [requests, setRequests] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, wRes] = await Promise.all([
        localApiAdapter.get('/api/approbations/requests'),
        localApiAdapter.get('/api/approbations/workflows'),
      ]);
      setRequests(Array.isArray(rRes?.data ?? rRes) ? (rRes?.data ?? rRes) : []);
      setWorkflows(Array.isArray(wRes?.data ?? wRes) ? (wRes?.data ?? wRes) : []);
    } catch (e) {
      console.error(e);
      setRequests([]);
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const approved = useMemo(() => countStatus(requests, 'approuve'), [requests]);
  const rejected = useMemo(() => countStatus(requests, 'rejete'), [requests]);
  const pending = useMemo(
    () => countStatus(requests, 'en_attente', 'en_cours'),
    [requests],
  );
  const approvalRate = requests.length ? Math.round((approved / requests.length) * 100) : 0;
  const pendingShare = requests.length ? Math.round((pending / requests.length) * 100) : 0;

  const activeWorkflows = useMemo(
    () => countStatus(workflows, 'actif'),
    [workflows],
  );
  const inactiveWorkflows = useMemo(
    () => countStatus(workflows, 'inactif'),
    [workflows],
  );
  const draftWorkflows = useMemo(
    () => countStatus(workflows, 'brouillon'),
    [workflows],
  );

  const byEntity = useMemo(() => {
    const map = {};
    requests.forEach((r) => {
      const k = entityLabel(r.entity_type, pl) || p('entity_other');
      map[k] = (map[k] || 0) + 1;
    });
    return Object.entries(map)
      .map(([label, value]) => ({
        label,
        value,
        display: requests.length
          ? `${value} (${Math.round((value / requests.length) * 100)}%)`
          : String(value),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [requests, pl, p]);

  const stats = useMemo(
    () => sliceQawaemStats([
      { label: p('stat_total'), value: requests.length, icon: BarChart3, variant: 'forest' },
      { label: p('stat_pending'), value: pending, icon: Clock, variant: 'copper' },
      { label: p('stat_approved'), value: approved, icon: CheckCircle, variant: 'forest' },
      { label: p('stat_rate'), value: `${approvalRate}%`, icon: TrendingUp, variant: 'copper' },
    ], 4),
    [requests.length, pending, approved, approvalRate, p],
  );

  const insights = useMemo(
    () => [
      {
        label: p('insight_approval_rate'),
        value: `${approvalRate}%`,
        hint: p('insight_approval_rate_hint', { approved, total: requests.length }),
      },
      {
        label: p('insight_avg_pending'),
        value: pendingShare > 0 ? `${pendingShare}%` : '0%',
        hint: p('insight_avg_pending_hint', { count: pending }),
      },
      {
        label: p('insight_active_workflows'),
        value: activeWorkflows,
        hint: p('insight_active_workflows_hint', { count: activeWorkflows, total: workflows.length }),
      },
    ],
    [approvalRate, approved, requests.length, pendingShare, pending, activeWorkflows, workflows.length, p],
  );

  const charts = useMemo(
    () => [
      {
        title: p('chart_entity_title'),
        subtitle: p('chart_entity_subtitle'),
        variant: 'ranked',
        rows: byEntity,
      },
      {
        title: p('chart_status_title'),
        subtitle: p('chart_status_subtitle'),
        variant: 'status',
        active: approved,
        inactive: pending + rejected,
      },
      {
        title: p('chart_workflows_title'),
        subtitle: p('chart_workflows_subtitle'),
        variant: 'pipeline',
        rows: pipelineFromKpis([
          { label: p('wf_active'), value: activeWorkflows },
          { label: p('wf_inactive'), value: inactiveWorkflows },
          { label: p('wf_draft'), value: draftWorkflows },
        ]),
      },
    ],
    [byEntity, approved, pending, rejected, activeWorkflows, inactiveWorkflows, draftWorkflows, p],
  );

  const table = useMemo(
    () => ({
      title: p('table_by_entity'),
      columns: [
        { key: 'entity', label: p('col_entity') },
        { key: 'count', label: p('col_count') },
        { key: 'share', label: p('col_share') },
      ],
      rows: byEntity.map(({ label, value }) => ({
        entity: label,
        count: value,
        share: requests.length ? `${Math.round((value / requests.length) * 100)}%` : '0%',
      })),
    }),
    [byEntity, requests.length, p],
  );

  return (
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={`${p('subtitle')} — ${p('workflows_count', { count: workflows.length })}`}
      loading={loading}
      onRefresh={loadData}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="approbations_analyses"
      emptyLabel={p('empty')}
    />
  );
}
